import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import { mdorderHeaders, orderHeaders, smorderHeaders } from "./orderHeaders";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { FAB } from "react-native-paper";

import { getOrders } from "../../redux/dispatcher/portalDispatcher";
import {
  setOrderCategoryFilter,
  setOrderPage,
  setOrderSearchQuery,
  setRequestType,
} from "../../redux/reducers/portalReducer";
import { PortalNestedDataTable } from "../../components/custom/DataTable/PortalNestedDataTable";
import { DynamicLabelBannerWithSearchBar } from "../../components/custom/Banners/DynamicLabelBannerWithSearchBar";

export default function CustomerOrderScreen({ navigation }: any) {
  const isFocused = useIsFocused();
  const dispatch = useDispatch<any>();

  // Get portal state from Redux
  const {
    orders,
    loading,
    searching,
    totalCount,
    totalPages,
    page,
    pageSize,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
  } = useSelector((state: any) => state.portal);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      orderHeaders,
    );

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const quickFilterList = [
    "all",
    "Request Delivery",
    "Made To Order",
    "Pick up",
    "Cancelled",
    "Pending For Approval",
    "Approved",
    "Ready",
    "Back-Order",
    "Shipped",
  ];

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);

  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const navigateToOrder = (order: any, action: string) => {
    navigation.navigate("CustomerOrderSummaryReport", { order });
  };

  // Fetch orders on mount and when filters change
  useEffect(() => {
    if (isFocused) {
      dispatch(getOrders() as any).unwrap();
    }
  }, [
    isFocused,
    dispatch,
    page,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
  ]);

  // Update table headers based on device size
  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? orderHeaders
        : isMediumDevice
          ? mdorderHeaders
          : smorderHeaders,
    );
  }, [isLargeDevice, isMediumDevice, isSmallDevice]);

  const screenWidth = Dimensions.get("window").width;

  const shouldUseCompactControls =
    isSmallDevice || isMobileDevice || screenWidth <= 1366;

  // Handle search
  const handleSearch = (query: string) => {
    dispatch(setOrderSearchQuery(query));
    dispatch(setOrderPage(0)); // reset page on search
  };

  const handleSearchTextChange = (text: string) => {
    dispatch(setOrderSearchQuery(text));
  };

  const handleQuickFilterChange = (selected: string[]) => {
    const requestTypes = ["Request Delivery", "Made To Order", "Pick up"];
    const selectedRequestTypes = selected.filter((s) =>
      requestTypes.includes(s),
    );
    const selectedStatuses = selected.filter((s) => !requestTypes.includes(s));

    dispatch(
      setRequestType(
        selectedRequestTypes.length === 0
          ? "all"
          : selectedRequestTypes.join(","),
      ),
    );
    dispatch(
      setOrderCategoryFilter(
        selectedStatuses.length === 0 ? "all" : selectedStatuses.join(","),
      ),
    );
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    dispatch(setOrderPage(newPage));
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(getOrders() as any);
  };

  const [selectedQuickFilters, setSelectedQuickFilters] = useState<string[]>(
    [],
  );
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);

  const columnsToNotHaveThousandSeparator: Array<string> = [
    "Packing #",
    "Hubspot ID",
    "Document Number",
    "Customer Number",
    "Order No",
  ];

  return (
    <View style={styles.container}>
      {isSmallDevice || isMobileDevice ? (
        <>
          {/* Mobile: Search bar full width on top */}
          <DynamicLabelBannerWithSearchBar
            titleName={`Total Items: ${totalCount || 0}`}
            setRefresh={handleRefresh}
            dataToExport={orders}
            fileName="orderReport"
            sheetName="Orders"
            disableTitle={false}
            noBorder
            handleSearch={handleSearch}
            onSearchTextChange={handleSearchTextChange}
            _searchText={searchQuery}
          />
          {/* Mobile: Dropdown filter button */}
          <View style={styles.mobileFilterWrapper}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <TouchableOpacity
                style={[
                  styles.mobileDropdownButton,
                  mobileFilterOpen && styles.mobileDropdownButtonActive,
                ]}
                onPress={() => setMobileFilterOpen(!mobileFilterOpen)}
              >
                <Text style={styles.mobileDropdownLabel}>Quick Filter: </Text>
                <Text style={styles.mobileDropdownValue}>
                  {selectedQuickFilters.length === 0
                    ? "All"
                    : selectedQuickFilters.length === 1
                      ? selectedQuickFilters[0]
                      : `${selectedQuickFilters.length} selected`}
                </Text>
                <Text style={styles.mobileDropdownArrow}>
                  {mobileFilterOpen ? "▲" : "▼"}
                </Text>
              </TouchableOpacity>
            </ScrollView>
            {mobileFilterOpen && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                {quickFilterList.map((option, index) => {
                  const isActive =
                    option === "all"
                      ? selectedQuickFilters.length === 0
                      : selectedQuickFilters.includes(option);
                  return (
                    <TouchableOpacity
                      key={index}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        if (option === "all") {
                          setSelectedQuickFilters([]);
                          handleQuickFilterChange([]);
                        } else {
                          const newFilters = selectedQuickFilters.includes(
                            option,
                          )
                            ? selectedQuickFilters.filter((f) => f !== option)
                            : [...selectedQuickFilters, option];
                          setSelectedQuickFilters(newFilters);
                          handleQuickFilterChange(newFilters);
                        }
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option === "all" ? "All" : option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}
          </View>
        </>
      ) : (
        /* Desktop: Quick Filter chips + search in one row */
        <View style={styles.quickFilterBar}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.quickFilterChipsContainer}
          >
            <Text style={styles.quickFilterLabel}>Quick Filter</Text>
            {quickFilterList.map((option, index) => {
              const isActive =
                option === "all"
                  ? selectedQuickFilters.length === 0
                  : selectedQuickFilters.includes(option);
              return (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.filterChip,
                    isActive && styles.filterChipActive,
                  ]}
                  onPress={() => {
                    if (option === "all") {
                      setSelectedQuickFilters([]);
                      handleQuickFilterChange([]);
                    } else {
                      const newFilters = selectedQuickFilters.includes(option)
                        ? selectedQuickFilters.filter((f) => f !== option)
                        : [...selectedQuickFilters, option];
                      setSelectedQuickFilters(newFilters);
                      handleQuickFilterChange(newFilters);
                    }
                  }}
                >
                  <Text
                    style={
                      isActive
                        ? styles.filterChipTextActive
                        : styles.filterChipText
                    }
                  >
                    {option === "all" ? "All" : option}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          <View style={styles.quickFilterSearchSection}>
            <DynamicLabelBannerWithSearchBar
              titleName={`Total Items: ${totalCount || 0}`}
              setRefresh={handleRefresh}
              dataToExport={orders}
              fileName="orderReport"
              sheetName="Orders"
              disableTitle={false}
              noBorder
              handleSearch={handleSearch}
              onSearchTextChange={handleSearchTextChange}
              _searchText={searchQuery}
            />
          </View>
        </View>
      )}

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading && !searching ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <FAB
              icon="arrow-up"
              style={[
                styles.fab,
                {
                  top:
                    contentOffSet +
                    Dimensions.get("window").height -
                    fabPosition,
                },
              ]}
              onPress={() =>
                listRef.current?.scrollTo({ x: 0, y: 0, animated: true })
              }
              small
            />
            <PortalNestedDataTable
              itemsPerPage={pageSize}
              isNestedDataLoaded={!loading}
              nestedHeader={tableHeaders}
              nestedRows={orders}
              rowKeyValue={"documentNumber"}
              key="rowKeyValue"
              nestedColumnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandSeparator
              }
              isDisplayTimeWithDate={false}
              handleNestedSortColumn={() => {}}
              disableHeader={false}
              operationActions={navigateToOrder}
              isDetail={false}
              setModalVisible={() => {}}
              handleRowClick={() => {}}
              setSelected={() => {}}
              // Server-side pagination props
              useServerPagination={true}
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              loading={searching}
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  quickFilterBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  quickFilterChipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  quickFilterLabel: {
    fontWeight: "700",
    fontSize: 13,
    color: "#374151",
    marginRight: 10,
  },
  quickFilterSearchSection: {
    flexShrink: 0,
    minWidth: 350,
  },
  mobileFilterWrapper: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#f8f9fa",
  },
  spinnerContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  filterChipActive: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterChipText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500" as const,
  },
  filterChipTextActive: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600" as const,
  },
});
